import {Injectable} from "@angular/core";
import {lastValueFrom, Observable, Subject} from "rxjs";
import {PerFormaAPIService, ProfileResponse, SessionDto} from "src/app/services/pfapi.service";
import {PerformaModulesService} from "src/app/services/performa-modules.service";

@Injectable({
    providedIn: "root"
})
export class AuthService {
    private _session: SessionDto = {};
    private _profile: ProfileResponse = {};
    private _initialized = false;

    // emits after session/profile are (re)loaded so the shell can rebuild user-dependent state
    // (nav, username, banners) — a fresh login navigates client-side without re-bootstrapping the
    // root component, so the shell must be told to refresh.
    private readonly _sessionChanged = new Subject<void>();
    public readonly sessionChanged$: Observable<void> = this._sessionChanged.asObservable();

    // local overrides for flags toggled at runtime (e.g. enrolling/unenrolling 2FA)
    private _twoFactorOverride: boolean | undefined;
    private _mustChangePassOverride: boolean | undefined;

    constructor(
        private readonly api: PerFormaAPIService,
        private readonly modules: PerformaModulesService
    ) {}

    // fetches session + profile when a token exists; called from APP_INITIALIZER. safe to call once.
    async init(): Promise<void> {
        if (this._initialized) return;
        this._initialized = true;
        await this.refresh();
    }

    // (re)loads session + profile from the current token. call after a fresh login so the new
    // user's flags (e.g. ForcePasswordChange) are available without a full page reload.
    async refresh(): Promise<void> {
        // a fresh login invalidates any in-session overrides and the previous user's cached modules
        this._twoFactorOverride = undefined;
        this._mustChangePassOverride = undefined;
        this.modules.reset();

        const token = localStorage.getItem("pf_token");
        if (!token) {
            this._session = {};
            this._profile = {};
            this._sessionChanged.next();
            return;
        }

        try {
            this._session = (await lastValueFrom(this.api.getApiAccountSession())) || {};
        } catch {
            // unauthenticated/expired token — leave session empty; guard/interceptor handle redirect
            this._session = {};
            this._sessionChanged.next();
            return;
        }

        try {
            // profile carries 2FA-enabled and force-password-change, which session does not
            this._profile = (await lastValueFrom(this.api.getApiProfile())) || {};
        } catch {
            this._profile = {};
        }

        this._sessionChanged.next();
    }

    public get isLoggedIn(): boolean {
        return !!localStorage.getItem("pf_token");
    }

    public get username(): string {
        return this._session.UserName || this._profile.Username || "";
    }

    public get email(): string {
        return this._session.Email || this._profile.Email || "";
    }

    public get isAdmin(): boolean {
        return !!this._session.IsAdmin;
    }

    public get isScanAdmin(): boolean {
        return !!this._session.IsScanAdmin;
    }

    // true when the user can upload at least one document type (fine-grained UploadFile right)
    public get canUpload(): boolean {
        return !!this._session.CanUpload;
    }

    public get isAPCheckAdmin(): boolean {
        return !!this._session.IsAPCheckAdmin;
    }

    public get isAPCheckUser(): boolean {
        return !!this._session.IsAPCheckUser;
    }

    public get isARReportUser(): boolean {
        return !!this._session.IsARReportUser;
    }

    public get isFINReportUser(): boolean {
        return !!this._session.IsFINReportUser;
    }

    public get twoFactorEnabled(): boolean {
        if (this._twoFactorOverride !== undefined) return this._twoFactorOverride;
        return !!this._profile.TwoFactorEnabled;
    }

    public set twoFactorEnabled(value: boolean) {
        this._twoFactorOverride = value;
    }

    public get mustChangePass(): boolean {
        if (this._mustChangePassOverride !== undefined) return this._mustChangePassOverride;
        return !!this._profile.ForcePasswordChange;
    }

    public set mustChangePass(value: boolean) {
        this._mustChangePassOverride = value;
    }

    // legacy "force email change" meant the user had no email on file — derive it the same way.
    public get mustSetEmail(): boolean {
        return (this._profile.Email || "").trim().length === 0;
    }

    // when the current password expires (null if not configured) — drives the shell expiry banner
    public get passwordExpiresOn(): Date | null {
        return this._session.PasswordExpiresOn ? new Date(this._session.PasswordExpiresOn) : null;
    }

    public get isPTPAdmin(): boolean {
        // PTP admin maps to the system CEO / store manager flags on the session
        return !!this._session.IsPTPSystemCEO || !!this._session.IsPTPStoreManager;
    }

    public get isSedona(): boolean {
        return (
            this.email.toLowerCase().includes("sedonatek.com") ||
            this.username.toLowerCase().includes("sedonatek.com")
        );
    }

    // true when the user still has pending consent forms (e.g. the AI opt-in form) to answer
    public get needsConsent(): boolean {
        return !!this._session.NeedsConsent;
    }
}
